
import { iDocument, iDataSource, iEngine, iTemplate, iAttribute, iFolder } from '@/modules/documentExecution/documentDetails/DocumentDetails'
import { defineComponent, PropType } from 'vue'
import { createValidations } from '@/helpers/commons/validationHelper'
import { AxiosResponse } from 'axios'
import { mapState } from 'vuex'
import { startOlap } from '../../dialogs/olapDesignerDialog/DocumentDetailOlapHelpers'
import mainDescriptor from '../../DocumentDetailsDescriptor.json'
import infoDescriptor from './DocumentDetailsInformationsDescriptor.json'
import useValidate from '@vuelidate/core'
import DatasetDialog from './DocumentDetailsDatasetDialog.vue'
import KnValidationMessages from '@/components/UI/KnValidatonMessages.vue'
import Card from 'primevue/card'
import Textarea from 'primevue/textarea'
import Dropdown from 'primevue/dropdown'
import InputSwitch from 'primevue/inputswitch'
import KnInputFile from '@/components/UI/KnInputFile.vue'
import DocumentDetailsTree from './DocumentDetailsTree.vue'

const crypto = require('crypto')

export default defineComponent({
    name: 'document-details-informations',
    components: { DatasetDialog, Card, Textarea, Dropdown, InputSwitch, KnValidationMessages, KnInputFile, DocumentDetailsTree },
    props: {
        selectedDocument: { type: Object as PropType<iDocument> },
        selectedDataset: { type: Object },
        availableStates: { type: Array },
        documentTypes: { type: Array as any, required: true },
        documentEngines: { type: Array as PropType<iEngine[]>, required: true },
        availableDatasources: { type: Array as PropType<iDataSource[]> },
        availableFolders: { type: Array as PropType<iFolder[]> },
        availableTemplates: { type: Array as PropType<iTemplate[]> },
        availableAttributes: { type: Array as PropType<iAttribute[]> }
    },
    emits: ['setTemplateForUpload', 'setImageForUpload', 'deleteImage', 'touched', 'openDesignerDialog'],
    computed: {
        filteredEngines(): any {
            if (this.document.typeCode) {
                return this.documentEngines.filter((engine) => engine.biobjTypeId === this.documentTypes.filter((type) => type.valueCd === this.document.typeCode)[0].valueId)
            }
            return []
        },
        isDataSourceVisible(): boolean {
            switch (this.document.engine) {
                case 'knowageofficeengine':
                case 'knowagecompositedoce':
                case 'knowageprocessengine':
                case 'knowagechartengine':
                case 'knowagenetworkengine':
                case 'knowagecockpitengine':
                case 'knowagedossierengine':
                case 'knowagekpiengine':
                case 'knowagesvgviewerengine':
                    return false
                default:
                    return true
            }
        },
        isDataSetVisible(): boolean {
            switch (this.document.engine) {
                case 'knowagegisengine':
                case 'knowagechartengine':
                case 'knowagenetworkengine':
                    return true
                default:
                    return false
            }
        },
        getImageUrl(): string {
            return process.env.VUE_APP_HOST_URL + `/knowage/servlet/AdapterHTTP?ACTION_NAME=MANAGE_PREVIEW_FILE_ACTION&SBI_ENVIRONMENT=DOCBROWSER&LIGHT_NAVIGATOR_DISABLED=TRUE&operation=DOWNLOAD&fileName=${this.selectedDocument?.previewFile}`
        },
        designerButtonVisible(): boolean {
            return this.document.typeCode == 'OLAP' || this.document.typeCode == 'KPI' || this.document.engine == 'knowagegisengine'
        },
        ...mapState({
            user: 'user'
        })
    },
    data() {
        return {
            v$: useValidate() as any,
            mainDescriptor,
            infoDescriptor,
            uploading: false,
            lockedByUser: false,
            triggerUpload: false,
            showDatasetDialog: false,
            triggerImageUpload: false,
            dataset: {} as any,
            folders: [] as iFolder[],
            document: {} as iDocument,
            templates: [] as iTemplate[],
            templateToUpload: { name: '' } as any,
            imageToUpload: { name: '' } as any,
            visibilityAttribute: '',
            restrictionValue: '',
            driversPositions: infoDescriptor.driversPositions,
            listOfTemplates: [] as iTemplate[],
            imagePreviewUrl: null as any,
            imagePreview: false
        }
    },
    async created() {
        this.setData()
        await this.getAllTemplates()
    },
    watch: {
        async selectedDocument() {
            this.setData()
            await this.getAllTemplates()
        }
    },
    validations() {
        const validationObject = { document: createValidations('document', infoDescriptor.validations.document) }
        return validationObject
    },
    methods: {
        setData() {
            this.templates = this.availableTemplates as iTemplate[]
            this.document = this.selectedDocument as iDocument
            this.dataset = this.selectedDataset
            this.folders = this.availableFolders as iFolder[]
            this.resetImagePreview()
            this.IsLockedByUser()
        },
        IsLockedByUser() {
            this.lockedByUser = this.document.lockedByUser === 'true' ? true : false
        },
        setIsLockedByUser() {
            this.document.lockedByUser = this.lockedByUser ? 'true' : 'false'
        },
        addRestriction() {
            if (this.document.profiledVisibility) {
                this.document.profiledVisibility = this.document.profiledVisibility + ' AND ' + this.visibilityAttribute + ' = ' + this.restrictionValue
            } else {
                this.document.profiledVisibility = this.visibilityAttribute + ' = ' + this.restrictionValue
            }
        },
        clearAllRestrictions() {
            this.document.profiledVisibility = ''
            this.visibilityAttribute = ''
            this.restrictionValue = ''
        },
        saveSelectedDataset(event) {
            this.document.dataSetId = event.id
            this.dataset = event
        },
        setUploadType() {
            this.triggerUpload = false
            setTimeout(() => (this.triggerUpload = true), 200)
        },
        setTemplateForUpload(event) {
            this.uploading = true
            this.templateToUpload = event.target.files[0]
            this.$emit('setTemplateForUpload', event.target.files[0])
            this.triggerUpload = false
            setTimeout(() => (this.uploading = false), 200)
        },
        setImageUploadType() {
            this.triggerImageUpload = false
            setTimeout(() => (this.triggerImageUpload = true), 200)
        },
        setImageForUpload(event) {
            this.uploading = true
            this.imageToUpload = event.target.files[0]
            this.$emit('setImageForUpload', event.target.files[0])
            this.setImagePreview(event.target.files[0])
            this.triggerImageUpload = false
            setTimeout(() => (this.uploading = false), 200)
        },
        setImagePreview(imageFile) {
            this.imagePreviewUrl = URL.createObjectURL(imageFile)
            this.imagePreview = true
            this.$store.commit('setInfo', { title: this.$t('common.uploadFileSuccess'), msg: this.$t('documentExecution.documentDetails.info.imageInfo') })
        },
        resetImagePreview() {
            this.imagePreviewUrl = null
            this.imagePreview = false
        },
        setFunctionality(event) {
            this.document.functionalities = event
        },
        onTypeChange() {
            this.$emit('touched')
            this.document.engine = ''
        },
        openDesignerConfirm() {
            this.$confirm.require({
                header: this.$t('common.toast.warning'),
                message: this.$t('documentExecution.olap.openDesignerMsg'),
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    switch (this.document.typeCode) {
                        case 'KPI':
                            this.openKpiDocumentDesigner()
                            break
                        case 'MAP': {
                            this.openGis()
                            break
                        }
                        default:
                            this.openDesigner()
                    }
                }
            })
        },
        async openDesigner() {
            if (this.listOfTemplates.length === 0) {
                this.$emit('openDesignerDialog')
            } else {
                const activeTemplate = this.findActiveTemplate()
                const sbiExecutionId = crypto.randomBytes(16).toString('hex')
                await startOlap(this.$http, this.user, sbiExecutionId, this.document, activeTemplate, this.$router)
            }
        },
        findActiveTemplate() {
            let activeTemplate = null as any
            for (let i = 0; i < this.listOfTemplates.length; i++) {
                if (this.listOfTemplates[i].active) {
                    activeTemplate = this.listOfTemplates[i]
                    break
                }
            }
            return activeTemplate
        },
        translatedLabel(a) {
            return this.$t(a.label)
        },
        openKpiDocumentDesigner() {
            this.$router.push(`/kpi-edit/${this.document.id}?from=documentDetail`)
        },
        openGis() {
            this.$router.push(`/gis/edit?documentId=${this.document.id}`)
        },
        async getAllTemplates() {
            if (this.document && this.document.id) this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `2.0/documentdetails/${this.document.id}/templates`).then((response: AxiosResponse<any>) => (this.listOfTemplates = response.data as iTemplate[]))
        }
    }
})
